import React, { useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { finalizeProductImageFile, createUniqueFileName } from '../../utils/imageCompression';
import { getPriceInputLabel } from '../../utils/orderDisplay';
import {
  canUseColorSizeMatrix,
  colorSizeMatrixToVariants,
  createEmptyColorSizeMatrix,
  guessColorSwatch,
  variantsToColorSizeMatrix,
} from '../../utils/productVariants';
import ImageCropperModal from '../Common/ImageCropperModal/ImageCropperModal';
import { PRODUCT_IMAGE_ASPECT_RATIO } from '../../constants/productImage';

const MAX_VARIANT_MEDIA = 5;

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();
const cellKey = (color, size) => `${normalizeKey(color)}::${normalizeKey(size || '')}`;

const ProductVariantsEditor = ({ variants = [], onChange, disabled = false, basePrice = '', adminData }) => {
  const useMatrix = useMemo(() => canUseColorSizeMatrix(variants), [variants]);
  const matrix = useMemo(() => {
    if (!useMatrix) return createEmptyColorSizeMatrix();
    return variantsToColorSizeMatrix(variants, basePrice);
  }, [variants, basePrice, useMatrix]);

  const colorHexRef = useRef({});
  const [, setHexRevision] = useState(0);

  const getRowHex = (colorName) => {
    const key = normalizeKey(colorName);
    if (colorHexRef.current[key]) return colorHexRef.current[key];
    return guessColorSwatch(colorName);
  };

  const [processingColorIndex, setProcessingColorIndex] = useState(null);
  const [cropQueue, setCropQueue] = useState(null);
  const [newSizeInput, setNewSizeInput] = useState('');

  const emitMatrix = (nextMatrix) => {
    onChange?.(colorSizeMatrixToVariants(nextMatrix, basePrice, variants));
  };

  const handleAddVariant = () => {
    const variantIndex = matrix.colors.length + 1;
    const defaultName = `Color ${variantIndex}`;
    const defaultHex = guessColorSwatch(defaultName);
    colorHexRef.current[normalizeKey(defaultName)] = defaultHex;

    const next = {
      ...matrix,
      colors: [...matrix.colors, { value: defaultName, media: [] }],
    };
    emitMatrix(next);
  };

  const handleRemoveColor = (index) => {
    const color = matrix.colors[index]?.value;
    const nextColors = matrix.colors.filter((_, i) => i !== index);
    const nextCells = { ...matrix.cells };
    Object.keys(nextCells).forEach((key) => {
      if (key.startsWith(`${normalizeKey(color)}::`)) delete nextCells[key];
    });
    delete colorHexRef.current[normalizeKey(color)];
    emitMatrix({ ...matrix, colors: nextColors, cells: nextCells });
  };

  const handleColorNameChange = (index, value) => {
    const prevName = matrix.colors[index]?.value;
    const prevKey = normalizeKey(prevName);
    const nextKey = normalizeKey(value);
    if (colorHexRef.current[prevKey] !== undefined) {
      colorHexRef.current[nextKey] = colorHexRef.current[prevKey];
      delete colorHexRef.current[prevKey];
    }

    const nextColors = matrix.colors.map((row, i) => (i === index ? { ...row, value } : row));
    const nextCells = { ...matrix.cells };
    const sizeList = matrix.sizes.length ? matrix.sizes : [''];
    sizeList.forEach((size) => {
      const oldKey = cellKey(prevName, size || '');
      const newKey = cellKey(value, size || '');
      if (nextCells[oldKey] !== undefined) {
        nextCells[newKey] = nextCells[oldKey];
        if (oldKey !== newKey) delete nextCells[oldKey];
      }
    });
    emitMatrix({ ...matrix, colors: nextColors, cells: nextCells });
  };

  const handleColorHexChange = (index, hex) => {
    const colorName = matrix.colors[index]?.value;
    if (!colorName) return;
    colorHexRef.current[normalizeKey(colorName)] = hex;
    setHexRevision((n) => n + 1);
  };

  const handleAddSize = () => {
    const trimmed = newSizeInput.trim();
    if (!trimmed) {
      toast.error('Enter a size label (e.g. S, M, L)');
      return;
    }
    if (matrix.sizes.some((s) => normalizeKey(s) === normalizeKey(trimmed))) {
      toast.error('This size already exists');
      return;
    }
    setNewSizeInput('');
    const nextSizes = [...matrix.sizes, trimmed];
    const nextCells = { ...matrix.cells };
    matrix.colors.forEach((colorRow) => {
      const key = cellKey(colorRow.value, trimmed);
      if (!nextCells[key]) {
        const colorOnlyKey = cellKey(colorRow.value, '');
        nextCells[key] = nextCells[colorOnlyKey] || {
          price: basePrice,
          isAvailable: true,
          sku: '',
        };
      }
    });
    emitMatrix({ ...matrix, sizes: nextSizes, cells: nextCells });
  };

  const handleRemoveSize = (sizeIndex) => {
    const size = matrix.sizes[sizeIndex];
    const nextSizes = matrix.sizes.filter((_, i) => i !== sizeIndex);
    const nextCells = { ...matrix.cells };
    Object.keys(nextCells).forEach((key) => {
      if (key.endsWith(`::${normalizeKey(size)}`)) delete nextCells[key];
    });
    emitMatrix({ ...matrix, sizes: nextSizes, cells: nextCells });
  };

  const handleCellChange = (colorValue, sizeValue, field, value) => {
    const key = cellKey(colorValue, sizeValue);
    const nextCells = {
      ...matrix.cells,
      [key]: {
        price: basePrice,
        isAvailable: true,
        sku: '',
        ...matrix.cells[key],
        [field]: value,
      },
    };
    emitMatrix({ ...matrix, cells: nextCells });
  };

  const openNextCrop = (files, colorIndex) => {
    const [first, ...rest] = files;
    if (!first) {
      setCropQueue(null);
      setProcessingColorIndex(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      setCropQueue({ src: readerEvent.target.result, colorIndex, remaining: rest });
    };
    reader.readAsDataURL(first);
  };

  const handleColorMediaSelect = (e, colorIndex) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || cropQueue) return;

    const currentCount = matrix.colors[colorIndex]?.media?.length || 0;
    if (currentCount + files.length > MAX_VARIANT_MEDIA) {
      toast.error(`Maximum ${MAX_VARIANT_MEDIA} images allowed per color`);
      return;
    }

    const invalid = files.find((file) => !file.type?.startsWith('image/') || file.size > 8 * 1024 * 1024);
    if (invalid && !invalid.type?.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    if (invalid) {
      toast.error('Each source image must be less than 8MB');
      return;
    }

    setProcessingColorIndex(colorIndex);
    openNextCrop(files, colorIndex);
  };

  const appendCroppedColorMedia = async (croppedFile, colorIndex) => {
    const finalImage = await finalizeProductImageFile(croppedFile);
    const namedImage = new File(
      [finalImage],
      createUniqueFileName(finalImage.name || 'variant.jpg'),
      { type: finalImage.type || 'image/jpeg', lastModified: Date.now() },
    );
    const previewUrl = URL.createObjectURL(namedImage);
    const nextColors = matrix.colors.map((row, i) => {
      if (i !== colorIndex) return row;
      return {
        ...row,
        media: [
          ...(row.media || []),
          {
            type: 'new',
            mediaType: 'image',
            url: previewUrl,
            thumbnail: previewUrl,
            file: namedImage,
            order: String((row.media || []).length + 1),
          },
        ],
      };
    });
    emitMatrix({ ...matrix, colors: nextColors });
  };

  const handleColorCropComplete = async (croppedFile) => {
    if (!cropQueue) return;
    const { colorIndex, remaining } = cropQueue;
    setCropQueue(null);
    try {
      await appendCroppedColorMedia(croppedFile, colorIndex);
      if (remaining.length) {
        openNextCrop(remaining, colorIndex);
      } else {
        setProcessingColorIndex(null);
      }
    } catch (err) {
      setProcessingColorIndex(null);
      toast.error(err.message || 'Failed to process image');
    }
  };

  const handleRemoveColorMedia = (colorIndex, mediaIndex) => {
    const nextColors = matrix.colors.map((row, i) => {
      if (i !== colorIndex) return row;
      const mediaItem = row.media[mediaIndex];
      if (mediaItem?.url?.startsWith('blob:')) URL.revokeObjectURL(mediaItem.url);
      return { ...row, media: row.media.filter((_, mIdx) => mIdx !== mediaIndex) };
    });
    emitMatrix({ ...matrix, colors: nextColors });
  };

  const sizeColumns = matrix.sizes.length ? matrix.sizes : [null];

  return (
    <div className="productlist-variants-section">
      <div className="productlist-variants-title-row">
        <span className="productlist-section-label">Product Variants</span>
      </div>

      {!useMatrix && variants.length > 0 && (
        <div className="productlist-variants-help productlist-variants-help-warn">
          <Info size={18} />
          <p>
            This product uses custom variant attributes. Remove existing variants and add color &amp; size variants
            below.
          </p>
        </div>
      )}

      <div className="productlist-variants-help">
        <Info size={18} className="productlist-variants-help-icon" />
        <div>
          <strong>Color &amp; size variants</strong>
          <p>
            Add each color variant with a swatch and name. Upload photos per color so shoppers see them when they pick
            that color. Add sizes if needed, then set price and stock for each combination.
          </p>
        </div>
      </div>

      <div className="productlist-matrix-block">
        <div className="productlist-matrix-block-header">
          <h4>Color variants</h4>
          {!disabled && useMatrix && (
            <button type="button" className="productlist-matrix-text-btn" onClick={handleAddVariant}>
              <Plus size={16} /> Add variant
            </button>
          )}
        </div>

        {matrix.colors.length === 0 && (
          <p className="productlist-matrix-empty">No variants yet. Add a variant to offer colors (and optional sizes).</p>
        )}

        {matrix.colors.map((colorRow, cIdx) => (
          <div key={`color-row-${cIdx}`} className="productlist-matrix-color-row">
            <div className="productlist-matrix-color-main">
              <label className="productlist-matrix-color-swatch-wrap" title="Pick color">
                <input
                  type="color"
                  className="productlist-matrix-color-swatch"
                  value={getRowHex(colorRow.value)}
                  onChange={(e) => handleColorHexChange(cIdx, e.target.value)}
                  disabled={disabled}
                />
              </label>
              <input
                type="text"
                className="productlist-matrix-color-input"
                value={colorRow.value}
                onChange={(e) => handleColorNameChange(cIdx, e.target.value)}
                placeholder="Color name (e.g. Red)"
                disabled={disabled}
              />
              {!disabled && matrix.colors.length > 0 && (
                <button
                  type="button"
                  className="productlist-variant-remove-btn"
                  onClick={() => handleRemoveColor(cIdx)}
                >
                  <Trash2 size={16} /> Remove
                </button>
              )}
            </div>
            <div className="productlist-variant-media-section">
              <label>Images for this color (optional, 1:1)</label>
              <div className="productlist-variant-media-grid">
                {!disabled && (
                  <label
                    className={`productlist-variant-media-add ${processingColorIndex === cIdx ? 'loading' : ''}`}
                  >
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleColorMediaSelect(e, cIdx)}
                      style={{ display: 'none' }}
                      disabled={disabled || processingColorIndex === cIdx}
                    />
                    {processingColorIndex === cIdx ? '...' : <Plus size={20} />}
                  </label>
                )}
                {(colorRow.media || []).map((media, mIdx) => (
                  <div key={`cm-${cIdx}-${mIdx}`} className="productlist-variant-media-item">
                    <img src={media.thumbnail || media.url} alt="" />
                    {!disabled && (
                      <button
                        type="button"
                        className="productlist-variant-media-remove"
                        onClick={() => handleRemoveColorMedia(cIdx, mIdx)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {!disabled && useMatrix && matrix.colors.length === 0 && (
          <button type="button" className="productlist-add-variant-btn productlist-matrix-add-first-variant" onClick={handleAddVariant}>
            <Plus size={18} /> Add variant
          </button>
        )}
      </div>

      {matrix.colors.length > 0 && (
        <div className="productlist-matrix-block">
          <div className="productlist-matrix-block-header">
            <h4>Sizes</h4>
            <span className="productlist-matrix-hint">Optional — skip if the product has no sizes</span>
          </div>
          <div className="productlist-matrix-sizes-row">
            {matrix.sizes.map((size, sIdx) => (
              <span key={size} className="productlist-matrix-size-chip">
                {size}
                {!disabled && (
                  <button type="button" onClick={() => handleRemoveSize(sIdx)} aria-label={`Remove size ${size}`}>
                    ×
                  </button>
                )}
              </span>
            ))}
            {!disabled && (
              <div className="productlist-matrix-add-size">
                <input
                  type="text"
                  value={newSizeInput}
                  onChange={(e) => setNewSizeInput(e.target.value)}
                  placeholder="e.g. M"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSize())}
                />
                <button type="button" onClick={handleAddSize}>
                  <Plus size={16} /> Add size
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {matrix.colors.length > 0 && (
        <div className="productlist-matrix-block">
          <h4>Pricing &amp; availability</h4>
          <div className="productlist-matrix-table-wrap">
            <table className="productlist-matrix-table">
              <thead>
                <tr>
                  <th>Color</th>
                  {sizeColumns.map((size) => (
                    <th key={size || 'default'}>{size || 'Price'}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.colors.map((colorRow, rowIdx) => (
                  <tr key={`price-row-${rowIdx}`}>
                    <td className="productlist-matrix-color-label">
                      <span
                        className="productlist-matrix-color-label-swatch"
                        style={{ backgroundColor: getRowHex(colorRow.value) }}
                        aria-hidden
                      />
                      {colorRow.value || '—'}
                    </td>
                    {sizeColumns.map((size) => {
                      const key = cellKey(colorRow.value, size || '');
                      const cell = matrix.cells[key] || {
                        price: basePrice,
                        isAvailable: true,
                      };
                      return (
                        <td key={cellKey(colorRow.value, size || '')}>
                          <div className="productlist-matrix-cell">
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={cell.price}
                              onChange={(e) =>
                                handleCellChange(colorRow.value, size || '', 'price', e.target.value)
                              }
                              placeholder={getPriceInputLabel(adminData)}
                              disabled={disabled}
                            />
                            <label className="productlist-matrix-stock-label">
                              <input
                                type="checkbox"
                                checked={cell.isAvailable !== false}
                                onChange={(e) =>
                                  handleCellChange(colorRow.value, size || '', 'isAvailable', e.target.checked)
                                }
                                disabled={disabled}
                              />
                              In stock
                            </label>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cropQueue?.src && (
        <ImageCropperModal
          imageSrc={cropQueue.src}
          aspectRatio={PRODUCT_IMAGE_ASPECT_RATIO}
          title="Crop variant image (1:1)"
          onClose={() => {
            setCropQueue(null);
            setProcessingColorIndex(null);
          }}
          onCropComplete={handleColorCropComplete}
        />
      )}
    </div>
  );
};

export default ProductVariantsEditor;
